/**
 * Copyright Mántum All Rights Reserved.
 */
import Label from '../label';
import Row from './row';
import debug from 'debug';
import store from '../internal/store';
import useSWR from 'swr';
import { source, helper, validator } from '@mantum/kanela';

import {
    forwardRef,
    useRef,
    useState,
    useEffect,
    useCallback,
    useMemo
} from 'react';

import {
    ACTION_DATASOURCE,
    ACTION_VALUE,
    EVENT_CHANGED,
    useGroup,
    useGroupDispatch
} from '../../group';

import type {
    DataSource,
    Filter,
    Option,
    Options,
    Result,
    Validation,
    Values
} from '@mantum/kanela';

const KEY_COMPLETE = 'complete';
const KEY_SIMPLE = 'simple';

const rProperty = /\{[a-z]+\}/i;

const info = debug('kanela:chips');

type Chip = {
    color?: string;
} & Options<string | number>;

export type PropTypes = {
    advanced?: {
        entity?: string;
        filter?: Filter;
        message?: string;
        options?: Option<string>;
        orientation?: 'vertical' | 'horizontal';
        preselect?: boolean;
        refresh?: number;
        result?: Result;
        revalidate?: boolean;
        selection?: 'simple' | 'multiple';
        type?: 'fat' | 'simple';
    };
    dataSource?: DataSource | Chip;
    disabled?: boolean;
    error?: boolean;
    label?: string;
    name?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (name?: string, value?: unknown) => void;
    optional?: boolean;
    testid?: string;
    validation?: Validation;
    value?: string | number | Values;
};

export default forwardRef<HTMLDivElement, PropTypes>(
    function Component(props, ref) {
        // prettier-ignore
        const {
            advanced = {},
            disabled = false,
            name,
        } = props

        const {
            entity = '',
            message = 'No data',
            orientation = 'vertical',
            preselect = false,
            refresh = 0,
            result = 'simple',
            revalidate = true,
            selection = 'simple',
            type = 'simple'
        } = advanced;

        const config = {
            refreshInterval: refresh,
            revalidateOnFocus: revalidate
        };

        const groups = useGroup();
        const dispatch = useGroupDispatch();

        const changeRef = useRef(props.onChange);
        const sourceRef = useRef<DataSource | null>(null);

        const [dataSource, setDataSource] = useState<
            Options<unknown> | DataSource
        >(props.dataSource || []);

        const values = useMemo(() => {
            const value = props.value ?? [];
            return Array.isArray(value) ? value : [value];
        }, [props.value]);

        const [currentValue, setCurrentValue] = useState<Values>(values);

        const fetcher = () => store(dataSource);

        const resolve = () => {
            return !Array.isArray(dataSource) &&
                !validator.isUndefined(dataSource.url)
                ? dataSource.url
                : null;
        };

        // prettier-ignore
        const {
            data = [], 
            isLoading 
        } = useSWR(resolve, fetcher, config);

        function getRows(values: unknown[], options?: Option<string>) {
            const label = options?.label
                .split('.')
                .filter((it) => it !== '') ?? ['label'];

            const value = options?.value
                .split('.')
                .filter((it) => it !== '') ?? ['value'];

            return values.map((it) => {
                const currentLabel = label?.reduce((previous, current) => {
                    return previous && previous[current as keyof unknown];
                }, it);

                const currentValue = value?.reduce((previous, current) => {
                    return previous && previous[current as keyof unknown];
                }, it);

                const color =
                    it && typeof it === 'object' && 'color' in it && it.color
                        ? `${it.color}`
                        : null;

                return {
                    label: `${currentLabel}`,
                    value: currentValue as string | number,
                    color: color
                };
            });
        }

        const rows = !Array.isArray(dataSource) ? data : dataSource;

        const items = useMemo(() => {
            return getRows(rows, props.advanced?.options);
        }, [rows, props.advanced?.options]);

        // const items = getRows(rows, props.advanced?.options);

        const getOriginal = useCallback(
            (selected: number | string) => {
                for (let index = 0; index < rows.length; index++) {
                    const element = rows[index];
                    if (element && typeof element === 'object') {
                        const result = source.extract(element, entity);
                        if (result === selected) {
                            return element;
                        }
                    }
                }
                return null;
            },
            [entity, rows]
        );

        const getValue = useCallback(
            (selected: number | string) => {
                if (!selected) {
                    return null;
                }

                if (result === KEY_SIMPLE) {
                    return selected;
                }

                let current = getOriginal(selected);
                if (current && result === KEY_COMPLETE) {
                    const prepare = helper.select.prepare(
                        [current],
                        props.advanced?.options
                    );

                    if (prepare.length > 0) {
                        current = prepare[0];
                    }
                }

                return current;
            },
            [result, getOriginal, props.advanced?.options]
        );

        const prepare = useCallback(
            (values: Values): unknown | unknown[] => {
                const results = values
                    .map((value) => getValue(value))
                    .filter((value) => value);

                if (selection === KEY_SIMPLE) {
                    return results.length > 0 ? results[0] : '';
                }
                return results;
            },
            [selection, getValue]
        );

        const applyFilter = useCallback(
            (values: Values) => {
                const filter = props.advanced?.filter;
                if (!filter || disabled) {
                    return;
                }

                if (values.length === 0) {
                    dispatch({
                        type: EVENT_CHANGED,
                        content: [
                            {
                                action: ACTION_DATASOURCE,
                                target: filter.target,
                                value: []
                            }
                        ]
                    });
                } else {
                    let dataSource = null;

                    if (validator.isDataSource(filter.dataSource)) {
                        const source = filter.dataSource as DataSource;
                        const parameter = source?.parameter ?? {};

                        let url = source.url;
                        if (rProperty.test(source.url)) {
                            values.forEach((key) => {
                                url = url.replace('{value}', `${key}`);
                            });
                        }

                        dataSource = {
                            ...source,
                            url,
                            parameter: parameter
                        };
                    }

                    dispatch({
                        type: EVENT_CHANGED,
                        content: [
                            {
                                action: ACTION_DATASOURCE,
                                target: filter.target,
                                value: dataSource,
                                clear: true
                            }
                        ]
                    });
                }
            },
            [dispatch, disabled, props.advanced?.filter]
        );

        useEffect(() => {
            changeRef.current = props.onChange;
        }, [props.onChange]);

        useEffect(() => {
            setCurrentValue(values);
        }, [values]);

        useEffect(() => {
            const dataSource = props.dataSource || [];
            if (Array.isArray(dataSource)) {
                return;
            }

            setDataSource((previous) => {
                const isChange =
                    Array.isArray(previous) ||
                    source.diff(dataSource, previous);

                if (isChange) {
                    return dataSource;
                }
                return previous;
            });
        }, [props.dataSource]);

        useEffect(() => {
            if (changeRef.current) {
                changeRef.current(name, prepare(values));
            }
        }, [name, values, prepare]);

        useEffect(() => {
            if (!name || !groups) {
                return;
            }

            if (!(name in groups)) {
                return;
            }

            const content = groups[name];
            info('dispatch: %o', content);
            if (content.action === ACTION_VALUE) {
                const values = (
                    Array.isArray(content.value)
                        ? content.value
                        : [content.value]
                ).filter((value) => value);

                info('dispatch: new value %o', values);
                setCurrentValue((previous) => {
                    if (JSON.stringify(previous) !== JSON.stringify(values)) {
                        return values;
                    }
                    return previous;
                });

                const results = prepare(values);
                if (changeRef.current) {
                    changeRef.current(name, results);
                }

                if (
                    !results ||
                    (Array.isArray(results) && results.length === 0)
                ) {
                    applyFilter([]);
                }
            } else if (content.action === ACTION_DATASOURCE) {
                if (Array.isArray(content.value)) {
                    info('dispatch: new datasource %o', content.value);
                    setDataSource(content.value);

                    if (content.clear) {
                        setCurrentValue([]);
                        if (changeRef.current) {
                            changeRef.current(name, []);
                        }
                    }
                } else if (validator.isDataSource(content.value)) {
                    const dataSource = content.value as DataSource;
                    info('dispatch: new datasource %o', dataSource);
                    setDataSource(dataSource);

                    if (isDiff(dataSource)) {
                        sourceRef.current = dataSource;
                        if (content.clear) {
                            setCurrentValue([]);
                            if (changeRef.current) {
                                changeRef.current(name, []);
                            }
                        }
                    }
                }
            }
        }, [name, groups, selection, prepare, applyFilter]);

        useEffect(() => {
            if (!preselect) {
                return;
            }

            if (selection !== KEY_SIMPLE) {
                return;
            }

            if (!Array.isArray(items) || items.length !== 1) {
                return;
            }

            const item = items[0];
            info('preselect value %o', item);

            const values = [item.value];
            const results = prepare(values);
            if (changeRef.current) {
                changeRef.current(name, results);
            }

            setCurrentValue(values);
            applyFilter(values);
        }, [preselect, selection, items, prepare, name, applyFilter]);

        const isDiff = (dataSource: DataSource) =>
            !sourceRef.current || source.diff(sourceRef.current, dataSource);

        const onChange = (selected: number | string) => {
            info('change: %o', selected);

            const isSelected = currentValue.includes(selected);

            let values;
            if (isSelected) {
                values = currentValue.filter((value) => value !== selected);
            } else {
                values =
                    selection === KEY_SIMPLE
                        ? [selected]
                        : [selected, ...currentValue];
            }

            setCurrentValue(values);

            const results = prepare(values);
            if (changeRef.current) {
                changeRef.current(name, results);
            }

            if (selection === KEY_SIMPLE) {
                applyFilter(values);
            }
        };

        const style = useMemo(() => {
            return orientation === 'horizontal' ? 'flex-row' : 'flex-col';
        }, [orientation]);

        return (
            <div className='flex w-full flex-col'>
                <Label
                    disabled={props.disabled}
                    name={props.label}
                    optional={props.optional}
                />
                <div
                    className={`flex flex-wrap gap-2 ${style}`}
                    data-testid={props.testid}
                    data-value={currentValue}
                    ref={ref}
                >
                    {items.length === 0 && (
                        <div className='flex w-full items-center justify-center'>
                            {message}
                        </div>
                    )}

                    {isLoading && (
                        <div className='flex w-full items-center justify-center'>
                            <svg
                                className='-ml-1 mr-3 h-5 w-5 animate-spin text-primary'
                                xmlns='http://www.w3.org/2000/svg'
                                fill='none'
                                viewBox='0 0 24 24'
                            >
                                <circle
                                    className='opacity-25'
                                    cx='12'
                                    cy='12'
                                    r='10'
                                    stroke='currentColor'
                                    strokeWidth='4'
                                />
                                <path
                                    className='opacity-75'
                                    fill='currentColor'
                                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                />
                            </svg>
                        </div>
                    )}
                    {items.map((it) => (
                        <Row
                            color={it.color}
                            disabled={props.disabled}
                            key={`${it.value}`}
                            label={it.label}
                            onChange={onChange}
                            type={type}
                            selected={
                                currentValue.findIndex(
                                    (value) => value === it.value
                                ) >= 0
                            }
                            value={it.value}
                        />
                    ))}
                </div>
            </div>
        );
    }
);
